import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { hasRole } from '@court-sync/shared';
import { useAuthStore } from './store/auth';
import { resolvePlatformDashboardRedirect } from './utils/platformLoginRedirect';
import { useCurrentAcademy } from './hooks/useDataStore';
import { useCurrentClub } from './hooks/useCurrentClub';
import { GTMPageViewTracker } from './components/GTMPageViewTracker';
import { DashboardLayout } from './components/layout/DashboardLayout';
import { GuardianAcademyLayout } from './components/layout/GuardianAcademyLayout';
import { CoachAcademyLayout } from './components/layout/CoachAcademyLayout';
// Coach Pages
import CoachAcademyPicker from './pages/dashboards/coach/CoachAcademyPicker';
import CoachDashboard from './pages/dashboards/coach/CoachDashboard';
import CoachSchedulePage from './pages/dashboards/coach/CoachSchedulePage';
import CoachLessonsPage from './pages/dashboards/coach/CoachLessonsPage';
import CoachRosterPage from './pages/dashboards/coach/CoachRosterPage';
import CoachStudentsPage from './pages/dashboards/coach/CoachStudentsPage';
import CoachStudentDetailPage from './pages/dashboards/coach/CoachStudentDetailPage';
import CoachProfilePage from './pages/dashboards/coach/CoachProfilePage';
import CoachAvailabilityPage from './pages/dashboards/coach/CoachAvailabilityPage';
import CoachAttendancePage from './pages/dashboards/coach/CoachAttendancePage';
import CoachInviteAcceptPage from './pages/dashboards/coach/CoachInviteAcceptPage';
import AdminInviteAcceptPage from './pages/dashboards/academy-admin/AdminInviteAcceptPage';
import ProtectedRoute from './components/ProtectedRoute';
// Auth & Setup Pages
import Login from './pages/Login';
import Register from './pages/Register';
import EmailVerification from './pages/EmailVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import FinishSignup from './pages/FinishSignup';
import AcademySetup from './pages/AcademySetup';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import LegalAcceptance from './pages/LegalAcceptance';

// Academy Owner Pages (lazy-loaded — large management surfaces)
const AcademyOwnerDashboard = lazy(() => import('./pages/dashboards/academy-owner/AcademyOwnerDashboard'));
const OwnerPostSubscriptionOnboarding = lazy(
  () => import('./pages/dashboards/academy-owner/OwnerPostSubscriptionOnboarding')
);
const OrganizationSettings = lazy(() => import('./pages/dashboards/organization/OrganizationSettings'));
const AcademySettings = lazy(() => import('./pages/dashboards/academy/AcademySettings'));
const LegacyClubSettingsRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacySettingsRedirect').then((m) => ({
    default: m.LegacyClubSettingsRedirect,
  }))
);
const LegacyOrgSettingsRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacySettingsRedirect').then((m) => ({
    default: m.LegacyOrgSettingsRedirect,
  }))
);
const LegacyAcademySettingsRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacySettingsRedirect').then((m) => ({
    default: m.LegacyAcademySettingsRedirect,
  }))
);
const LegacyPlayEventKioskRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyPlayEventKioskRedirect,
  }))
);
const LegacyPlayEventBoardRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyPlayEventBoardRedirect,
  }))
);
const LegacyCourtsLocationRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyCourtsLocationRedirect,
  }))
);
const LegacyStudentDetailRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyStudentDetailRedirect,
  }))
);
const LegacyPlayEventDetailRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyPlayEventDetailRedirect,
  }))
);
const LegacyOrganizationSettingsPathRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyOrganizationSettingsPathRedirect,
  }))
);
const LegacyAcademySettingsPathRedirect = lazy(() =>
  import('./pages/dashboards/academy-owner/settings/LegacyManagementRedirects').then((m) => ({
    default: m.LegacyAcademySettingsPathRedirect,
  }))
);
const CourtManagement = lazy(() => import('./pages/dashboards/academy-owner/courts/CourtManagement'));
const LocationCourts = lazy(() => import('./pages/dashboards/academy-owner/courts/LocationCourts'));
const CoachManagement = lazy(() => import('./pages/dashboards/academy-owner/staff/CoachManagement'));
const AcademyAdminManagement = lazy(() => import('./pages/dashboards/academy-owner/staff/AcademyAdminManagement'));
const TermManagement = lazy(() => import('./pages/dashboards/academy-owner/terms/TermManagement'));
const Calendar = lazy(() => import('./pages/dashboards/academy-owner/calendar/Calendar'));
const ProgramManagement = lazy(() => import('./pages/dashboards/academy-owner/programs/ProgramManagement'));
const CampManagement = lazy(() => import('./pages/dashboards/academy-owner/camps/CampManagement'));
const CampBulkWizard = lazy(() => import('./pages/dashboards/academy-owner/camps/CampBulkWizard'));
const BookingManagement = lazy(() => import('./pages/dashboards/academy-owner/bookings/BookingManagement'));
const PaymentManagement = lazy(() => import('./pages/dashboards/academy-owner/payments/PaymentManagement'));
const StudentManagementPage = lazy(() => import('./pages/dashboards/academy-owner/students/StudentManagementPage'));
const StudentDetailPage = lazy(() => import('./pages/dashboards/academy-owner/students/StudentDetailPage'));

// Club Pages (lazy-loaded)
const ClubCourtHire = lazy(() => import('./pages/dashboards/club/ClubCourtHire'));
const ClubMembers = lazy(() => import('./pages/dashboards/club/ClubMembers'));
const ClubInventory = lazy(() => import('./pages/dashboards/club/ClubInventory'));
const PlayEventsPage = lazy(() => import('./pages/dashboards/play/PlayEventsPage'));
const PlayEventDetailPage = lazy(() => import('./pages/dashboards/play/PlayEventDetailPage'));
const PlayEventSeriesPage = lazy(() => import('./pages/dashboards/play/PlayEventSeriesPage'));
const PlayEventKioskPage = lazy(() => import('./pages/dashboards/play/PlayEventKioskPage'));
const PlayEventBoardRedirectPage = lazy(() => import('./pages/dashboards/play/PlayEventBoardRedirectPage'));

// Student/Guardian Pages
import StudentDashboard from './pages/dashboards/student/StudentDashboard';
import OrgPickerPage from './pages/dashboards/student/OrgPickerPage';
import LessonDetail from './pages/dashboards/student/lessons/LessonDetail';
import BookLesson from './pages/dashboards/student/bookings/BookLesson';
import MyBookings from './pages/dashboards/student/bookings/MyBookings';
import MyCalendar from './pages/dashboards/student/bookings/MyCalendar';
import StudentCreditsPage from './pages/dashboards/student/credits/StudentCreditsPage';
import Profile from './pages/dashboards/student/profile/Profile';
import MyStudents from './pages/dashboards/student/profile/MyStudents';

// Shared Pages
import Settings from './pages/shared/Settings';
import Notifications from './pages/shared/Notifications';

// Backoffice/Admin Pages
import RiskPage from './pages/backoffice/RiskPage';
import RiskDetailPage from './pages/backoffice/RiskDetailPage';
import NotificationsPage from './pages/backoffice/NotificationsPage';
import PaymentsPage from './pages/backoffice/PaymentsPage';
import PayoutsPage from './pages/backoffice/PayoutsPage';
import SupportListPage from './pages/backoffice/SupportListPage';
import SupportDetailPage from './pages/backoffice/SupportDetailPage';
import SubscriptionCouponsPage from './pages/backoffice/SubscriptionCouponsPage';
import UsersPage from './pages/backoffice/UsersPage';
import UserDetailPage from './pages/backoffice/UserDetailPage';
import EmailTesterPage from './pages/backoffice/EmailTesterPage';
import WebhookEventsPage from './pages/backoffice/WebhookEventsPage';
import AccountingSyncAttemptsPage from './pages/backoffice/AccountingSyncAttemptsPage';
import CampaignsPage from './pages/backoffice/CampaignsPage';
import CampaignEditorPage from './pages/backoffice/CampaignEditorPage';
import CampaignDetailPage from './pages/backoffice/CampaignDetailPage';
import FeatureFlagsPage from './pages/backoffice/FeatureFlagsPage';

// Public Pages (White-label) - Stripe pages lazy-loaded to avoid "Cannot access before initialization" in dev
const PaymentPage = lazy(() => import('./pages/PaymentPage'));

const DashboardRouteFallback = (
  <div className="flex items-center justify-center min-h-[200px]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
  </div>
);

import CheckoutSuccess from './pages/public/CheckoutSuccess';
import CheckoutCancel from './pages/public/CheckoutCancel';
import SubscriptionCheckoutSuccess from './pages/SubscriptionCheckoutSuccess';
import SubdomainTenantApp from './SubdomainTenantApp';
import { isTenantSubdomainHost } from './lib/tenantHost';
import { orgPathModeRouteElements } from './routes/OrgPublicRoutes';

const tenantSubdomainV2Enabled =
  import.meta.env.VITE_TENANT_SUBDOMAIN_V2 !== 'false';

/** Unified owner/admin dashboard shell (academy + club management in one sidenav). */
function ManagementDashboardLayout() {
  const { user } = useAuthStore();
  const { currentAcademy } = useCurrentAcademy();
  const { currentClub } = useCurrentClub();
  const dashboardRole = hasRole(user, 'academy_owner')
    ? 'academy_owner'
    : hasRole(user, 'academy_admin')
      ? 'academy_admin'
      : hasRole(user, 'club_owner')
        ? 'club_owner'
        : 'club_admin';

  return (
    <DashboardLayout
      dashboardRole={dashboardRole}
      academySlug={currentAcademy?.slug}
      clubName={currentClub?.name}
      clubLogoUrl={currentClub?.logoUrl}
    >
      <Suspense fallback={DashboardRouteFallback}>
        <Outlet />
      </Suspense>
    </DashboardLayout>
  );
}

// Dashboard router: redirects to role-appropriate dashboard URL
function DashboardRouter() {
  const { user } = useAuthStore();

  if (!user) {
    return null;
  }

  const redirect = resolvePlatformDashboardRedirect(user);
  if (redirect.path === '/academy-setup') {
    return <Navigate to={redirect.path} state={redirect.state} replace />;
  }
  return <Navigate to={redirect.path} replace />;
}

function App() {
  if (tenantSubdomainV2Enabled && isTenantSubdomainHost()) {
    return <SubdomainTenantApp />;
  }

  return (
    <>
      <GTMPageViewTracker />
      <div className="min-h-svh bg-white dark:bg-gray-900">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/legal-acceptance" element={<LegalAcceptance />} />
          <Route path="/email-verification" element={<EmailVerification />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/academy-setup" element={<AcademySetup />} />
          <Route path="/finish-signup" element={<FinishSignup />} />
          <Route path="/payment" element={<Suspense fallback={<div className="flex items-center justify-center min-h-[200px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" /></div>}><PaymentPage /></Suspense>} />

          {orgPathModeRouteElements()}

          {/* Checkout Routes */}
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />

          {/* Subscription Checkout Return Routes */}
          <Route path="/payment/success" element={<SubscriptionCheckoutSuccess />} />

          {/* Protected Routes with Dashboard Layout */}

          {/* Main Dashboard - redirects to role-appropriate URL */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardRouter /></ProtectedRoute>} />

          {/* Organization picker - guardians/students/members with no org context */}
          <Route
            path="/org-picker"
            element={
              <ProtectedRoute requireRole={['guardian', 'student', 'member']}>
                <OrgPickerPage />
              </ProtectedRoute>
            }
          />
          <Route path="/academy-picker" element={<Navigate to="/org-picker" replace />} />

          {/* Legacy guardian academy routes (redirect to org portal over time) */}
          <Route
            path="/academy/:academySlug"
            element={
              <ProtectedRoute requireRole={['guardian', 'student']}>
                <GuardianAcademyLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<StudentDashboard />} />
            <Route path="lesson/:id" element={<LessonDetail />} />
            <Route path="book-lesson/:lessonId" element={<BookLesson />} />
            <Route path="calendar" element={<MyCalendar />} />
            <Route path="bookings" element={<MyBookings />} />
            <Route path="schedule" element={<Navigate to="calendar" replace />} />
            <Route path="credits" element={<StudentCreditsPage />} />
            <Route path="payments" element={<Navigate to="bookings" replace />} />
            <Route path="profile" element={<Profile />} />
            <Route path="settings" element={<Settings />} />
            <Route path="my-students" element={<MyStudents />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          {/* Public Coach Invite Acceptance */}
          <Route path="/coach/invite/:token" element={<CoachInviteAcceptPage />} />
          <Route path="/admin-invite/:token" element={<AdminInviteAcceptPage />} />

          {/* Coach Academy Picker - for coaches linked to multiple academies */}
          <Route path="/coach/academy-picker" element={
            <ProtectedRoute requireRole="coach"><CoachAcademyPicker /></ProtectedRoute>
          } />

          {/* Coach Academy-Scoped Routes - mirrors guardian pattern */}
          <Route
            path="/academy/:academySlug/coach"
            element={
              <ProtectedRoute requireRole="coach">
                <CoachAcademyLayout />
              </ProtectedRoute>
            }
          >
            <Route path="dashboard" element={<CoachDashboard />} />
            <Route path="schedule" element={<CoachSchedulePage />} />
            <Route path="availability" element={<CoachAvailabilityPage />} />
            <Route path="lessons" element={<CoachLessonsPage />} />
            <Route path="roster" element={<CoachRosterPage />} />
            <Route path="attendance" element={<CoachAttendancePage />} />
            <Route path="students" element={<CoachStudentsPage />} />
            <Route path="students/:studentId" element={<CoachStudentDetailPage />} />
            <Route path="profile" element={<CoachProfilePage />} />
            <Route path="settings" element={<Settings />} />
            <Route path="notifications" element={<Notifications />} />
          </Route>

          <Route
            path="/subscription"
            element={<Navigate to="/settings?tab=subscription" replace />}
          />

          {/* Play event kiosk — fullscreen, outside dashboard chrome */}
          <Route
            path="/admin/play-events/:eventId/kiosk"
            element={
              <ProtectedRoute
                requireRole={['academy_owner', 'academy_admin', 'club_owner', 'club_admin']}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-screen items-center justify-center bg-[#0b0d12]">
                      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-amber-400" />
                    </div>
                  }
                >
                  <PlayEventKioskPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/play-events/:eventId/kiosk"
            element={
              <ProtectedRoute
                requireRole={['academy_owner', 'academy_admin', 'club_owner', 'club_admin']}
              >
                <Suspense fallback={null}>
                  <LegacyPlayEventKioskRedirect />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/play-events/:eventId/board"
            element={
              <ProtectedRoute
                requireRole={['academy_owner', 'academy_admin', 'club_owner', 'club_admin']}
              >
                <Suspense
                  fallback={
                    <div className="flex min-h-screen items-center justify-center bg-[#0b0d12]">
                      <div className="h-10 w-10 animate-spin rounded-full border-2 border-white/20 border-t-amber-400" />
                    </div>
                  }
                >
                  <PlayEventBoardRedirectPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/club/play-events/:eventId/board"
            element={
              <ProtectedRoute
                requireRole={['academy_owner', 'academy_admin', 'club_owner', 'club_admin']}
              >
                <Suspense fallback={null}>
                  <LegacyPlayEventBoardRedirect />
                </Suspense>
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/onboarding"
            element={
              <ProtectedRoute requireRole="academy_owner">
                <Suspense
                  fallback={
                    <div className="flex min-h-screen items-center justify-center">
                      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
                    </div>
                  }
                >
                  <OwnerPostSubscriptionOnboarding />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/academy-owner/onboarding"
            element={<Navigate to="/admin/onboarding" replace />}
          />

          {/* Organization management (academy + club) with unified DashboardLayout */}
          <Route
            element={
              <ProtectedRoute
                requireRole={['academy_owner', 'academy_admin', 'club_owner', 'club_admin']}
              >
                {/* eslint-disable-next-line jsx-a11y/aria-role */}
                <ManagementDashboardLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/admin/dashboard" element={<AcademyOwnerDashboard />} />
            <Route path="/admin/organization/settings" element={<OrganizationSettings />} />
            <Route path="/admin/academy/settings" element={<AcademySettings />} />
            <Route path="/admin/courts/location/:locationId" element={<LocationCourts />} />
            <Route path="/admin/courts" element={<CourtManagement />} />
            <Route path="/admin/staff" element={<CoachManagement />} />
            <Route path="/admin/admins" element={<AcademyAdminManagement />} />
            <Route path="/admin/terms" element={<TermManagement />} />
            <Route path="/admin/calendar" element={<Calendar />} />
            <Route path="/admin/lessons" element={<Navigate to="/admin/calendar" replace />} />
            <Route path="/admin/programs" element={<ProgramManagement />} />
            <Route
              path="/admin/programs/:programId/interests"
              element={<Navigate to="/admin/bookings?tab=eoi" replace />}
            />
            <Route path="/admin/camps" element={<CampManagement />} />
            <Route path="/admin/camps/new" element={<CampBulkWizard />} />
            <Route path="/admin/bookings" element={<BookingManagement />} />
            <Route
              path="/admin/private-lesson-requests"
              element={<Navigate to="/admin/bookings?tab=private-requests" replace />}
            />
            <Route path="/admin/payments" element={<PaymentManagement />} />
            <Route path="/admin/students" element={<StudentManagementPage />} />
            <Route path="/admin/students/:studentId" element={<StudentDetailPage />} />
            <Route path="/admin/court-hire" element={<ClubCourtHire />} />
            <Route path="/admin/members" element={<ClubMembers />} />
            <Route path="/admin/inventory" element={<ClubInventory />} />
            <Route path="/admin/play-events" element={<PlayEventsPage />} />
            <Route
              path="/admin/play-events/new"
              element={<Navigate to="/admin/play-events?create=1" replace />}
            />
            <Route path="/admin/play-events/series" element={<PlayEventSeriesPage />} />
            <Route path="/admin/play-events/:eventId" element={<PlayEventDetailPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/notifications" element={<Notifications />} />

            {/* Legacy redirects — academy-owner prefix */}
            <Route path="/academy-owner/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/academy-owner/settings" element={<LegacyOrgSettingsRedirect />} />
            <Route path="/academy-owner/academy" element={<LegacyAcademySettingsRedirect />} />
            <Route path="/academy-owner/courts/location/:locationId" element={<LegacyCourtsLocationRedirect />} />
            <Route path="/academy-owner/courts" element={<Navigate to="/admin/courts" replace />} />
            <Route path="/academy-owner/staff" element={<Navigate to="/admin/staff" replace />} />
            <Route path="/academy-owner/admins" element={<Navigate to="/admin/admins" replace />} />
            <Route path="/academy-owner/terms" element={<Navigate to="/admin/terms" replace />} />
            <Route path="/academy-owner/calendar" element={<Navigate to="/admin/calendar" replace />} />
            <Route path="/academy-owner/lessons" element={<Navigate to="/admin/calendar" replace />} />
            <Route path="/academy-owner/programs" element={<Navigate to="/admin/programs" replace />} />
            <Route
              path="/academy-owner/programs/:programId/interests"
              element={<Navigate to="/admin/bookings?tab=eoi" replace />}
            />
            <Route path="/academy-owner/camps" element={<Navigate to="/admin/camps" replace />} />
            <Route path="/academy-owner/camps/new" element={<Navigate to="/admin/camps/new" replace />} />
            <Route path="/academy-owner/bookings" element={<Navigate to="/admin/bookings" replace />} />
            <Route
              path="/academy-owner/private-lesson-requests"
              element={<Navigate to="/admin/bookings?tab=private-requests" replace />}
            />
            <Route path="/academy-owner/payments" element={<Navigate to="/admin/payments" replace />} />
            <Route path="/academy-owner/students" element={<Navigate to="/admin/students" replace />} />
            <Route path="/academy-owner/students/:studentId" element={<LegacyStudentDetailRedirect />} />

            {/* Legacy redirects — club operator prefix */}
            <Route path="/club/dashboard" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/club/settings" element={<LegacyClubSettingsRedirect />} />
            <Route path="/club/calendar" element={<Navigate to="/admin/calendar" replace />} />
            <Route path="/club/court-hire" element={<Navigate to="/admin/court-hire" replace />} />
            <Route path="/club/members" element={<Navigate to="/admin/members" replace />} />
            <Route path="/club/inventory" element={<Navigate to="/admin/inventory" replace />} />
            <Route path="/club/play-events" element={<Navigate to="/admin/play-events" replace />} />
            <Route
              path="/club/play-events/new"
              element={<Navigate to="/admin/play-events?create=1" replace />}
            />
            <Route path="/club/play-events/series" element={<Navigate to="/admin/play-events/series" replace />} />
            <Route path="/club/play-events/:eventId" element={<LegacyPlayEventDetailRedirect />} />
            <Route path="/club/payments" element={<Navigate to="/admin/payments" replace />} />

            {/* Legacy redirects — settings paths */}
            <Route path="/organization/settings" element={<LegacyOrganizationSettingsPathRedirect />} />
            <Route path="/academy/settings" element={<LegacyAcademySettingsPathRedirect />} />
          </Route>

          {/* Admin/Backoffice Routes with DashboardLayout */}
          <Route
            element={
              <ProtectedRoute requireRole="admin">
                {/* eslint-disable-next-line jsx-a11y/aria-role */}
                <DashboardLayout dashboardRole="admin" />
              </ProtectedRoute>
            }
          >
            <Route path="/backoffice/risk" element={<RiskPage />} />
            <Route path="/backoffice/risk/:academyId" element={<RiskDetailPage />} />
            <Route path="/backoffice/notifications" element={<NotificationsPage />} />
            <Route path="/backoffice/payments" element={<PaymentsPage />} />
            <Route path="/backoffice/webhook-events" element={<WebhookEventsPage />} />
            <Route path="/backoffice/accounting-sync" element={<AccountingSyncAttemptsPage />} />
            <Route path="/backoffice/payouts" element={<PayoutsPage />} />
            <Route path="/backoffice/support" element={<SupportListPage />} />
            <Route path="/backoffice/support/:id" element={<SupportDetailPage />} />
            <Route path="/backoffice/subscription-coupons" element={<SubscriptionCouponsPage />} />
            <Route path="/backoffice/users" element={<UsersPage />} />
            <Route path="/backoffice/users/:userId" element={<UserDetailPage />} />
            <Route path="/backoffice/email-tester" element={<EmailTesterPage />} />
            <Route path="/backoffice/campaigns" element={<CampaignsPage />} />
            <Route path="/backoffice/campaigns/new" element={<CampaignEditorPage />} />
            <Route path="/backoffice/campaigns/:id" element={<CampaignDetailPage />} />
            <Route path="/backoffice/feature-flags" element={<FeatureFlagsPage />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/notifications" element={<Notifications />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<div className="text-center mt-12">404 - Page Not Found</div>} />
        </Routes>
      </div>
    </>
  );
}

export default App;
